import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Settings,
  UserCog,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  QrCode,
  Bell,
  CreditCard,
  Shield,
  Zap,
  Key,
  LayoutGrid,
  Leaf,
  Lock,
  ShoppingBag,
  BarChart3,
  LogOut,
  Users,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils';
import { useCurrentUser } from '@/hooks/redux';
import { UserRole } from '@/types';
import { useLogoutMutation } from '@/store/api/authApi';
import { useDispatch } from 'react-redux';
import { clearAuth } from '@/store/slices/authSlice';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavItem {
  name: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  badge?: string;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  // { name: 'Dashboard', href: '/dashboard', icon: LayoutGrid },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Open Content', href: '/open-content', icon: Leaf },
  { name: 'Locked Content', href: '/locked-content', icon: Lock },
  { name: 'Add Notification', href: '/add-notification', icon: Bell },
  { name: 'User Management', href: '/user-management', icon: Users },
  { name: 'Guest User', href: '/guest-users', icon: Users },
  { name: 'Profile', href: '/profile', icon: UserCog },
];

interface NavItemComponentProps {
  item: NavItem;
  isOpen: boolean;
  level?: number;
}

function NavItemComponent({ item, isOpen, level = 0 }: NavItemComponentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const user = useCurrentUser();
  const isAdmin = user?.role === UserRole.ADMIN;

  // Don't render admin-only items for non-admin users
  if (item.adminOnly && !isAdmin) {
    return null;
  }

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.href ? location.pathname === item.href : false;
  const hasActiveChild = item.children?.some(
    (child) => child.href && location.pathname.startsWith(child.href)
  );

  // Auto-expand if has active child
  React.useEffect(() => {
    if (hasActiveChild && isOpen) {
      setIsExpanded(true);
    }
  }, [hasActiveChild, isOpen]);

  const handleClick = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const Icon = item.icon;
  const indent = level * 16;

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={handleClick}
          className={cn(
            'flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors text-white',

            !isOpen && 'justify-center'
          )}
          style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
          title={!isOpen ? item.name : undefined}
        >
          <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
          {isOpen && (
            <>
              <span className="flex-1 text-left text-white">{item.name}</span>
              {item.badge && (
                <span className="ml-2 inline-flex items-center rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium text-white">
                  {item.badge}
                </span>
              )}
              {isExpanded ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </>
          )}
        </button>

        {/* Submenu */}
        {hasChildren && isExpanded && isOpen && (
          <div className="mt-1 space-y-1">
            {item.children!.map((child) => (
              <NavItemComponent key={child.name} item={child} isOpen={isOpen} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Regular nav item
  return (
    <NavLink
      to={item.href!}
      className={({ isActive }) =>
        cn(
          'flex items-center  px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? ' text-white border-l-2 border-white'
            : 'text-white/50 border-left border-white',
          !isOpen && 'justify-center'
        )
      }
      style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
      title={!isOpen ? item.name : undefined}
    >
      <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
      {isOpen && (
        <>
          <span>{item.name}</span>
          {item.badge && (
            <span className="ml-auto inline-flex items-center rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium">
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export function EnhancedSidebar({ isOpen, onToggle }: SidebarProps) {
  const user = useCurrentUser();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [logoutApi] = useLogoutMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleLogout = async () => {
    try {
      await logoutApi().unwrap();
    } catch (error) {
      console.error('Logout API failed:', error);
    } finally {
      dispatch(clearAuth());
      navigate('/login', { replace: true });
    }
  };
  return (
    <>
      {/* Sidebar backdrop for mobile */}
      {isOpen && <div className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={onToggle} />}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex flex-col bg-darkPurple border-r transition-all duration-300 overflow-hidden',
          isOpen ? 'w-60 translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'
        )}
      >
        {/* Sidebar header */}
        <div className="flex h-24 items-center  px-4">
          <div
            className={cn('flex items-center ', isOpen ? 'w-full border-b py-6' : 'justify-center')}
          >
            {isOpen && (
              <div className="ml-3 flex-1 ">
                <img src="/logo.svg" alt="" />
              </div>
            )}
          </div>

          {/* Toggle button - desktop only */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn(
              'flex h-6 w-6 text-white bg-none hover:bg-none',
              isOpen ? 'ml-auto ' : 'fixed left-2 top-3 z-40 '
            )}
          >
            {isOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-4 px-2  pt-10 overflow-y-auto">
          {navigation.map((item) => (
            <NavItemComponent key={item.name} item={item} isOpen={isOpen} />
          ))}
        </nav>
        <div className="flex mt-3 space-x-2">
          <button
            onClick={handleLogout}
            aria-label="Logout"
            className="flex-1 text-white px-3 py-2 text-lg font-medium rounded-lg hover:bg-white/10 transition-colors flex items-center justify-center"
          >
            <LogOut className="w-4 h-4 mr-1" />
            {isOpen && 'Logout'}
          </button>
        </div>
      </div>
    </>
  );
}
